import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { products } from '../data/products';
import { useAccessibility } from '../hooks/useAccessibility';

interface ProductSelectionPageProps {
  onBack: () => void;
}

type KitVariant = 'flashing-kit' | 'crack-repair';
type SelectedProduct =
  | { type: 'flexcrete'; size: '½ Gallon' | '1 Gallon' | '5 Gallon' }
  | { type: 'kit'; variant: KitVariant };

const flexcreteVariants = [
  {
    size: '½ Gallon' as const,
    coverage: 'Coverage: 20 to 25 sq ft',
    stripeUrl: 'https://buy.stripe.com/14A6oHeCf8uw3D3ctm9oc01',
    price: '$36.50',
  },
  {
    size: '1 Gallon' as const,
    coverage: 'Coverage: 40 to 50 sq ft',
    stripeUrl: 'https://buy.stripe.com/14A9AT51F2680qR2SM9oc03',
    price: '$67',
  },
  {
    size: '5 Gallon' as const,
    coverage: 'Coverage: 200 to 250 sq ft',
    stripeUrl: 'https://buy.stripe.com/9B63cv9hVaCE7Tjctm9oc02',
    price: '$360',
  },
];

const flexcreteDescription = `Flexcrete is a 2-component, waterproof cementitious mortar. The liquid component is a blend of polymers while the dry component is a proprietary blend of cement and other admixtures. When mixed and applied, one achieves a waterproof membrane with incredible adhesion.`;

const flexcreteFeatures = [
  'Maximum Adhesion - Bonds to all substrates',
  'Designed for submerged applications',
  'Works on positive and negative water pressure',
  'Impervious to UV rays',
  'Resists all corrosive agents',
];

const flashingKitProduct = products.find(p => p.id === 'flashing-kit');
const kitListPrice = flashingKitProduct?.price ?? '$58.50';

const kitVariants = [
  {
    id: 'flashing-kit' as KitVariant,
    label: 'Flashing Kit',
    coverage: 'Coverage: 10 linear ft',
    price: kitListPrice,
  },
  {
    id: 'crack-repair' as KitVariant,
    label: 'Crack Repair',
    coverage: 'Coverage: 10 linear ft',
    price: kitListPrice,
  },
];

const getSelectionTitle = (p: SelectedProduct): string => {
  if (p.type === 'flexcrete') {
    return `Flexcrete Waterproofing — ${p.size}`;
  }
  return p.variant === 'flashing-kit' ? 'Flashing Kit' : 'Crack Repair';
};

const getSelectionPrice = (p: SelectedProduct): string => {
  if (p.type === 'flexcrete') {
    return flexcreteVariants.find(v => v.size === p.size)?.price ?? '';
  }
  return kitVariants.find(kv => kv.id === p.variant)?.price ?? kitListPrice;
};

const infoSections = [
  {
    id: 'packaging-coverage',
    title: 'Packaging & Coverage',
    icon: '📦',
    color: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-400',
    product: products.find(p => p.id === 'packaging-coverage'),
  },
  {
    id: 'mixing-preparing',
    title: 'Mixing & Preparing',
    icon: '🥄',
    color: 'from-green-50 to-green-100',
    borderColor: 'border-green-400',
    product: products.find(p => p.id === 'mixing-preparing'),
  },
  {
    id: 'technical-specifications',
    title: 'Technical Specifications & Chemical Resistance',
    icon: '📊',
    color: 'from-purple-50 to-purple-100',
    borderColor: 'border-purple-400',
    product: products.find(p => p.id === 'technical-specifications'),
  },
];

const SELECTION_MESSAGE = 'Product has been selected';

const ProductSelectionPage: React.FC<ProductSelectionPageProps> = ({ onBack }) => {
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [expandedInfo, setExpandedInfo] = useState<string | null>(null);
  const addToCartRef = useRef<HTMLButtonElement>(null);
  const pendingScrollAfterSelectRef = useRef(false);
  const { announce } = useAccessibility();

  const handleProductOptionSelect = (next: SelectedProduct) => {
    pendingScrollAfterSelectRef.current = true;
    setSelectedProduct(next);
    const title = getSelectionTitle(next);
    const price = getSelectionPrice(next);
    announce(`${SELECTION_MESSAGE}: ${title}, ${price}`);
  };

  useEffect(() => {
    if (!selectedProduct || !pendingScrollAfterSelectRef.current) return;
    pendingScrollAfterSelectRef.current = false;

    const scrollTimer = window.setTimeout(() => {
      addToCartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    return () => {
      window.clearTimeout(scrollTimer);
    };
  }, [selectedProduct]);

  const handleAddToCart = () => {
    if (!selectedProduct) return;
    if (selectedProduct.type === 'flexcrete') {
      const variant = flexcreteVariants.find(v => v.size === selectedProduct.size);
      if (variant) window.open(variant.stripeUrl, '_blank');
    } else if (selectedProduct.type === 'kit' && flashingKitProduct?.stripeCheckoutUrl) {
      window.open(flashingKitProduct.stripeCheckoutUrl, '_blank');
    }
  };

  const getDetailTitle = () =>
    selectedProduct ? getSelectionTitle(selectedProduct) : '';

  const getDetailPrice = () =>
    selectedProduct ? getSelectionPrice(selectedProduct) : '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Select Product</h1>
          <p className="text-lg text-gray-500">Choose a product and size to get started</p>
          {selectedProduct && (
            <motion.div
              key={
                selectedProduct.type === 'flexcrete'
                  ? `flexcrete-${selectedProduct.size}`
                  : `kit-${selectedProduct.variant}`
              }
              role="status"
              aria-live="polite"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 mx-auto max-w-md rounded-xl bg-green-50 px-5 py-4 text-center ring-1 ring-green-200 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-green-700">
                {SELECTION_MESSAGE}
              </p>
              <p className="mt-2 text-base font-semibold text-gray-900 leading-snug">
                {getSelectionTitle(selectedProduct)}
              </p>
              <p className="mt-1 text-xl font-bold text-primary-600">
                {getSelectionPrice(selectedProduct)}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* ─── Tree Diagram ─── */}
        <div className="flex flex-col gap-6 mb-12">

          {/* ── Flexcrete Waterproofing Row ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col md:flex-row items-start gap-4"
          >
            {/* Category box */}
            <div className="flex-shrink-0 w-full md:w-56">
              <div className="bg-white border-2 border-primary-500 rounded-xl p-5 shadow-md text-center">
                <div className="text-3xl mb-2">💧</div>
                <h2 className="text-lg font-bold text-gray-900">Flexcrete Waterproofing</h2>
              </div>
            </div>

            {/* Connector line (desktop) */}
            <div className="hidden md:flex items-center self-center">
              <div className="w-8 h-0.5 bg-gray-400" />
            </div>

            {/* Size boxes */}
            <div className="flex flex-col gap-3 flex-1">
              {flexcreteVariants.map((variant, idx) => {
                const isSelected =
                  selectedProduct?.type === 'flexcrete' &&
                  selectedProduct.size === variant.size;
                return (
                  <div key={variant.size} className="flex items-center gap-2">
                    {/* Vertical + horizontal connector */}
                    <div className="hidden md:flex flex-col items-center self-stretch mr-1">
                      {idx < flexcreteVariants.length - 1 ? (
                        <div className="w-0.5 flex-1 bg-gray-400" />
                      ) : (
                        <div className="w-0.5 h-1/2 bg-gray-400" />
                      )}
                      <div className="w-4 h-0.5 bg-gray-400 self-center" />
                      {idx < flexcreteVariants.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-400" />
                      )}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() =>
                        handleProductOptionSelect({ type: 'flexcrete', size: variant.size })
                      }
                      className={`flex-1 flex items-center justify-between px-5 py-3 rounded-xl border-2 transition-all duration-200 shadow-sm text-left ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-gray-900">{variant.size}</span>
                        <p className="text-sm text-gray-500 mt-0.5">{variant.coverage}</p>
                      </div>
                      <span
                        className={`text-lg font-bold ${
                          isSelected ? 'text-primary-600' : 'text-gray-700'
                        }`}
                      >
                        {variant.price}
                      </span>
                    </motion.button>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* ── Kit Row ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col md:flex-row items-start gap-4"
          >
            {/* Category box */}
            <div className="flex-shrink-0 w-full md:w-56">
              <div className="bg-white border-2 border-purple-500 rounded-xl p-5 shadow-md text-center">
                <div className="text-3xl mb-2">🔧</div>
                <h2 className="text-lg font-bold text-gray-900">Kit</h2>
              </div>
            </div>

            {/* Connector line (desktop) */}
            <div className="hidden md:flex items-center self-center">
              <div className="w-8 h-0.5 bg-gray-400" />
            </div>

            {/* Kit variant boxes */}
            <div className="flex flex-col gap-3 flex-1">
              {kitVariants.map((kv, idx) => {
                const isSelected =
                  selectedProduct?.type === 'kit' &&
                  selectedProduct.variant === kv.id;
                return (
                  <div key={kv.id} className="flex items-center gap-2">
                    <div className="hidden md:flex flex-col items-center self-stretch mr-1">
                      {idx < kitVariants.length - 1 ? (
                        <div className="w-0.5 flex-1 bg-gray-400" />
                      ) : (
                        <div className="w-0.5 h-1/2 bg-gray-400" />
                      )}
                      <div className="w-4 h-0.5 bg-gray-400 self-center" />
                      {idx < kitVariants.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-400" />
                      )}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleProductOptionSelect({ type: 'kit', variant: kv.id })}
                      className={`flex-1 flex items-center justify-between px-5 py-3 rounded-xl border-2 transition-all duration-200 shadow-sm text-left ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                      }`}
                    >
                      <div>
                        <span className="font-semibold text-gray-900">{kv.label}</span>
                        <p className="text-sm text-gray-500 mt-0.5">{kv.coverage}</p>
                      </div>
                      <span
                        className={`text-lg font-bold ${
                          isSelected ? 'text-purple-600' : 'text-gray-700'
                        }`}
                      >
                        {kv.price}
                      </span>
                    </motion.button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* ─── Product Detail Panel ─── */}
        {selectedProduct && (
          <motion.div
            key={JSON.stringify(selectedProduct)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-12"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{getDetailTitle()}</h2>

                {selectedProduct.type === 'flexcrete' && (
                  <div>
                    <p className="text-gray-600 mb-4">{flexcreteDescription}</p>
                    <ul className="space-y-2">
                      {flexcreteFeatures.map((f, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <span className="text-primary-600 mr-2 mt-0.5">•</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedProduct.type === 'kit' && flashingKitProduct && (
                  <div>
                    <p className="text-gray-600 mb-4">{flashingKitProduct.description}</p>
                    <ul className="space-y-2">
                      {flashingKitProduct.features.slice(0, 5).map((f, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <span className="text-purple-600 mr-2 mt-0.5">•</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-start sm:items-end gap-3 sm:min-w-[160px]">
                <div className="text-3xl font-bold text-primary-600">{getDetailPrice()}</div>
                <span className="text-sm font-medium text-green-600">In Stock</span>
                <button
                  ref={addToCartRef}
                  onClick={handleAddToCart}
                  className="btn-primary flex items-center gap-2 px-6 py-3 text-base w-full sm:w-auto justify-center"
                >
                  <ShoppingCartIcon className="w-5 h-5" />
                  Add to Cart
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── Info Sections ─── */}
        <div className="border-t border-gray-200 pt-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Product Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {infoSections.map((section, idx) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                className={`bg-gradient-to-br ${section.color} border-l-4 ${section.borderColor} rounded-xl shadow-sm overflow-hidden`}
              >
                <button
                  onClick={() =>
                    setExpandedInfo(expandedInfo === section.id ? null : section.id)
                  }
                  className="w-full text-left p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{section.icon}</span>
                      <h3 className="font-semibold text-gray-900 text-sm leading-snug">
                        {section.title}
                      </h3>
                    </div>
                    <span className="text-gray-500 text-lg ml-2">
                      {expandedInfo === section.id ? '▲' : '▼'}
                    </span>
                  </div>
                </button>

                {expandedInfo === section.id && section.product && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-5 pb-5"
                  >
                    <p className="text-sm text-gray-700 mb-4">{section.product.description}</p>
                    {section.product.specifications.slice(0, 6).map((spec, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-xs text-gray-600 py-1 border-b border-white/60 last:border-0"
                      >
                        <span className="font-medium">{spec.label}</span>
                        <span className="text-right ml-4">{spec.value}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionPage;
